import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { Pressable, ScrollView, StyleSheet, TextInput as RNTextInput, View } from 'react-native';
import { BasicScrollView } from "@/components/BasicScrollView";
import { ArrowIcon, Button as Button95, Checkbox, Hourglass, NumberInput, Panel, TextInput } from 'react95-native-rabbl';
import { AppText } from '@/components/AppText';
import Head from 'expo-router/head';
import { CustomText } from '@/components/CustomText';
import Footer from '@/components/Footer';
import * as AppUtilities from "@/utilities/AppUtilities";
import * as AppUtils from '@/utilities/AppUtils';
import * as AppConstants from "@/utilities/AppConstants";
import * as AppConsts from "@/utilities/AppConsts";
import { useForm, Controller } from "react-hook-form";
import { AppContext } from '@/components/AppContext';
import { CommunityMembership } from "@/types/community_membership";
import { RelativePathString, useLocalSearchParams, useRouter } from 'expo-router';
import type { RootState } from '@/state/store';
import { useSelector, useDispatch } from 'react-redux';
import { setData } from '@/state/user';
import { Meta } from '@/components/Meta';
import { Image } from 'expo-image';
import { Ethnicity } from '@/types/profile';

const styles = StyleSheet.create({
  container: {
    flex: 1,
    padding: 20,
    paddingTop: 10,
    gap: 20,
    backgroundColor: 'transparent',
    alignItems: 'center',
  },
  formElement: {
    width: '100%',
  },
  inputWrapper: {
    gap: 10,
    alignItems: 'center',
    width: '100%',
  },
  buttonWrapper: {
    flexDirection: 'row',
  },
  panel: {
    padding: 20,
    gap: 20,
    minWidth: AppConsts.BASIC_MINIMUM_WIDTH,
    maxWidth: AppConsts.MAX_WIDTH_COMFORTABLE_READING,
  },
  numericWrapper: {
    flexDirection: 'row',
    gap: 5,
  },
  numeric: {
    width: 80,
  },
  flexRow: {
    flexDirection: "row",
    gap: 20,
    alignSelf: "center",
  },
});

export default function OptionalSettings() {
  const domain = useSelector((state: RootState) => state.user.domain);
  const { showSpinner, hideSpinner } = useContext(AppContext);
  const router = useRouter();
  const dispatch = useDispatch();
  const communityMembership = useSelector((state: RootState) => state.user.communityMembership);
  const { edit } = useLocalSearchParams<{ edit?: string }>();

  const onSubmit = useCallback(
    async (skip: boolean) => {
      try {
        showSpinner();

        const response = await AppUtilities.fetchAuthorized(
          "OptionalSettings.onSubmit",
          "/rabbl/api/optionalsettings",
          "POST",
          {
            domain: domain,
          }
        );
        const membership: CommunityMembership = ((response as any).body as any).community_membership;
        dispatch(setData(response));
        if (edit && edit.length > 0 && router.canGoBack()) {
          router.back();
        } else {
          if (skip) {
            router.navigate(AppUtils.getProfileCreateNextPageLink(membership) as RelativePathString);
          } else {
            router.navigate(AppUtils.getProfileCreateNextOptionalPageLink("optionalsettings", membership) as RelativePathString);
          }
        }
      } catch (e) {
        AppUtils.showErrorToast(AppUtilities.getErrors(e), () => onSubmit(skip), true);
        AppUtilities.handleException(e, "OptionalSettings.onSubmit");
      } finally {
        hideSpinner();
      }
    },
    [
      router,
      domain,
    ]
  );

  return (
    <BasicScrollView center={true}>
      <Meta title={"Optional Settings"} />
            
      <Panel variant='raised' elevation={4}>
        <View style={styles.panel}>

          <View style={AppConstants.STYLES.alignCenter}>
            <CustomText>Optional</CustomText>
          </View>

          <AppText>Do you want to fill out optional info for your profile?</AppText>

          <AppText>Dating intentions, children, ethnicity, religion and more.</AppText>

          <View style={styles.flexRow}>
            <View style={styles.buttonWrapper}>
              <Button95
                onPress={() => onSubmit(false)}
              >{ edit ? "Save" : "Yes" }</Button95>
            </View>

            <View style={styles.buttonWrapper}>
              <Button95
                onPress={() => onSubmit(true)}
              >Skip for now</Button95>
            </View>
          </View>
        </View>
      </Panel>
      
      <Footer />
    </BasicScrollView>
  );
}
